import { Icon, Input } from '@southfields-digital/mxmz-react-ui';
import { useEffect } from 'react';

import ResolutionMismatchWarning from 'src/components/ResolutionMismatchWarning';

import styles from './CanvasGraphicsForm.module.scss';

const CANVAS_COLUMN_WIDTH = 150;

interface IRundownFormProps {
  title: string;
  canvases: Canvas[];
  graphics: Graphic[];
  state: RundownTemplateCanvas[];
  stateUpdated: (state: (RundownTemplateCanvas | RundownCanvas)[]) => void;
  showToggleAll?: boolean;
}

export default function CanvasGraphicsForm({
  title,
  canvases,
  graphics,
  state,
  stateUpdated,
  showToggleAll = true,
}: IRundownFormProps) {
  useEffect(() => {
    if (state && canvases.length && state.length !== canvases.length) {
      configureState(state, canvases);
    }
  }, [state, canvases]);

  const isChecked = (graphic: Graphic, relatedCanvas?: RundownTemplateCanvas) =>
    relatedCanvas ? relatedCanvas.graphics.some(({ id }) => id === graphic.id) : false;

  const getCheckedGraphics = (
    rundownTemplateCanvas: RundownTemplateCanvas
  ): { graphic: Graphic; checked: boolean }[] =>
    graphics.map((graphic) => ({
      graphic,
      checked: isChecked(graphic, rundownTemplateCanvas),
    }));

  const configureState = (state: (RundownTemplateCanvas | RundownCanvas)[], canvases: Canvas[]) => {
    const updatedState = canvases.map((canvas) => {
      const existingCanvas = state.find(({ id }) => id === canvas.id);
      if (existingCanvas) return existingCanvas;

      return { id: canvas.id, name: canvas.name, graphics: [], canvasBackgroundId: null };
    });

    stateUpdated(updatedState);
  };

  const handleToggleGraphic = (canvas: Canvas, graphic: Graphic) => {
    if (!state) return;

    const canvasId = canvas.id;
    const updatedState: RundownTemplateCanvas[] = state.map((canvas) => {
      if (canvas.id !== canvasId) return canvas;

      const existingGraphic = canvas.graphics.some(({ id }) => id === graphic.id);
      const graphicsToInject: Graphic[] = [graphic];

      if (graphic.types.includes('bundle')) {
        graphicsToInject.push(...getChildGraphics(graphic.id));
      }

      const graphics: Graphic[] = existingGraphic
        ? canvas.graphics.filter(({ id }) => !graphicsToInject.find((g) => g.id === id))
        : [...canvas.graphics, ...graphicsToInject];

      return { ...canvas, graphics };
    }, []);

    if (stateUpdated) {
      stateUpdated(updatedState);
    }
  };

  const getChildGraphics = (parentGraphicId: string) =>
    graphics.filter((graphic) => graphic.parentId === parentGraphicId);

  const toggleAllGraphics = (isChecked: boolean, targetCanvas: Canvas) => {
    if (!state) return;

    const updatedState = state.map((canvas) => {
      if (canvas.id !== targetCanvas.id) return canvas;

      const newGraphics = isChecked
        ? canvas.graphics.concat(
            graphics.filter((graphic) => !canvas.graphics.some((g) => g.id === graphic.id))
          )
        : canvas.graphics.filter((graphic) => !graphics.some((g) => g.id === graphic.id));

      return { ...canvas, graphics: newGraphics };
    });

    stateUpdated?.(updatedState);
  };

  const allChecked = (canvas: Canvas) => {
    if (!state) return false;

    const relatedCanvas = state.find(({ id }) => id === canvas.id);
    if (!relatedCanvas) return false;

    const canvasGraphics = getCheckedGraphics(relatedCanvas);
    if (!canvasGraphics.length) return false;

    return canvasGraphics.every(({ checked }) => checked);
  };

  return (
    <div className={styles.CanvasGraphicsContainer}>
      <table
        style={{ minWidth: `${canvases.length * CANVAS_COLUMN_WIDTH}px` }}
        className={styles.CanvasGraphicsForm}
      >
        <thead>
          <tr>
            <th className={styles.GraphicColumn}>{title}</th>
            {canvases?.map((canvas, index) => (
              <th key={index} className={styles.CanvasGraphicColumn}>
                <div className={styles.Name} title={canvas.name}>
                  {canvas.name}
                </div>
                <div className={styles.Resolution}>
                  {canvas.width}×{canvas.height}
                </div>
              </th>
            ))}
          </tr>
          {showToggleAll && (
            <tr className={styles.CanvasGraphicToggleAllRow}>
              <th>Toggle all</th>
              {canvases?.map((canvas, index) => {
                const allGraphicsChecked = allChecked(canvas);
                return (
                  <th key={index} className={styles.CanvasGraphicColumn}>
                    <Input
                      classNameWrapper={styles.CanvasGraphicsToggle}
                      size="sm"
                      type="toggle"
                      onChange={() => toggleAllGraphics(!allGraphicsChecked, canvas)}
                      checked={!!allGraphicsChecked}
                    />
                  </th>
                );
              })}
            </tr>
          )}
        </thead>
        <tbody>
          {graphics?.map((graphic) => (
            <tr key={graphic.id}>
              <td className={styles.GraphicColumn}>
                {graphic.parentId ? <Icon icon="minus" weight="bold" /> : null}
                <div>
                  <div className={styles.Name} title={graphic.name}>
                    {graphic.name}
                  </div>
                  {graphic.config.resolution && (
                    <div className={styles.Resolution}>
                      {graphic.config.resolution.width}×{graphic.config.resolution.height}
                    </div>
                  )}
                </div>
              </td>

              {canvases?.map((canvas) => {
                const relatedCanvas = state?.find(({ id }) => id === canvas.id);
                const checked = isChecked(graphic, relatedCanvas);
                return (
                  <td className={styles.CanvasGraphicColumn} key={`${graphic.id}-${canvas.id}`}>
                    <div className={styles.CanvasGraphicCell}>
                      <Input
                        key={`${graphic.id}-${canvas.id}-input`}
                        classNameWrapper={styles.CanvasGraphicsToggle}
                        checked={!!checked}
                        onChange={() => handleToggleGraphic(canvas, graphic)}
                        disabled={graphic.parentId !== null}
                        size="sm"
                        type="toggle"
                      />
                      {checked && (
                        <ResolutionMismatchWarning
                          graphic={graphic}
                          canvas={canvas}
                          className={styles.CanvasGraphicWarning}
                        />
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
